import React, { useEffect, useState, forwardRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Col, Row, Spinner, DropdownItem, Card } from "reactstrap";
import { VsiBubble, VsiH4 } from "../../../styled/App.styled";
import { useVsiAppContext } from "../../../stores/VsiApp";
import { enterprisePerformanceDefs } from "../../../utils/gridColumnDefs/storage/allUnits";
import VerticalBarChart from "../../../components/VerticalBarChart";
import Grid from "../../../components/Grid";
import ReactDatePicker from "react-datepicker";
import { dateFromPeriod, periodFromDate } from "../../../utils/Helpers";
import style from "../../../utils/themeStyles";
import { useDataWithRoute, useTitleFromComponent, useUrlState } from "../../../utils/hooks";
import CustomDateInput from "../../../components/CustomDateInput";

import "react-datepicker/dist/react-datepicker.css";
import VsiDropdown from "../../../components/VsiDropdown";
import VsiLabeledSelect from "../../../components/VsiLabeledSelect";
import { VsiSelectLabel } from "../../../styled/VsiLabeledSelect.styled";

const titles = ["Used Growth (GB)", "IOPs Growth"];

const allBus = [
  { value: "device_type", label: "Device Type", field: "device_type_code" },
  { value: "data_center", label: "Data Center", field: "data_center" },
  { value: "classification", label: "Classification", field: "classification" },
  { value: "operational_category", label: "Operational Category", field: "operational_category" }
];

export default function EnterprisePerformance({ context, title }) {
  const [isLoading, setIsLoading] = useState(true);
  const { theme, client, periodId } = useVsiAppContext();

  const [devices, setDevices] = useState([]);
  const [chartSeries, setChartSeries] = useState([]);

  const [dateList, setDateList] = useState([]);
  const [presetsOpen, setPresetsOpen] = useState(false);
  const [maxMin, setMaxMin] = useState({});
  const [enterprisePerformance, setEnterprisePerformance] = useState([]);
  const [rangesValue, setRangesValue] = useState("1 Month");
  const [rangeOptions, setRangeOptions] = useState([]);

  let navigate = useNavigate();

  useTitleFromComponent(`${context} / ${title}`);

  const [params, setParams] = useUrlState({
    type: "device_type",
    start_period: 0,
    end_period: periodId
  });

  const { type, start_period: startPeriod, end_period: endPeriod } = params;

  const [startDate, setStartDate] = useState(new Date(dateFromPeriod(startPeriod)));
  const [endDate, setEndDate] = useState(new Date(dateFromPeriod(endPeriod)));

  let start = periodFromDate(startDate);
  let end = periodFromDate(endDate);

  const [{
    recordsets: data
  }] = useDataWithRoute(`/api/v6/report/enterprise_monthly_performance_proc?client_id=${client.clientId}&beginning_period_id=${start}&end_period_id=${end}`, Boolean(end));

  const selectedType = allBus.find(bu => bu.value === type);

  useEffect(() => {
    if (startPeriod && endPeriod && data.length) {
      getEnterprisePerformance(data);
    }
  }, [data, startPeriod, endPeriod, type]);

  useEffect(() => {
    if (data.length) {
      let dateOpts = [];
      const datesRange = data?.[1]?.map?.(dateObj => {
        let date = new Date(dateObj?.collection_date);
        // Compensate for timezone differences
        date = new Date(date.getTime() - date.getTimezoneOffset() * -60000);
        return date;
      });

      data?.[2]?.forEach?.(date => {
        if (date.month_val > 0) {
          dateOpts.push({
            label: date.month_val === 1 ? date.month_val + " Month" : date.month_val + " Months",
            value: date.formatted_collection_date
          });
        }
      });
      if (dateOpts.length === 0) {
        setRangesValue("Ranges");
      }
      setDateList(datesRange);
      setRangeOptions(dateOpts);
      // Setting oldest date as Start Date
      var oldestDate;
      if (dateOpts) {
        oldestDate = dateOpts?.[0]?.value;
      } else {
        oldestDate = data?.[2]?.[1]?.formatted_collection_date;
      }

      if (oldestDate && !startPeriod) {
        setDate(new Date(oldestDate), "start");
      }
    }
  }, [data]);

  const getGraphData = function(dataGrid, uniqueDevices) {
    let newData = [];
    for (let i = 0; i < uniqueDevices.length; i++) {
      let iopsGrowth = 0;
      let totalUsedGrowth = 0;
      for (let y = 0; y < dataGrid.length; y++) {
        if (uniqueDevices[i] === dataGrid[y][selectedType.field || "device_type_code"]) {
          iopsGrowth = iopsGrowth + dataGrid[y].iops_growth;
          totalUsedGrowth = totalUsedGrowth + dataGrid[y].total_used_growth;
        }
      }
      newData.push({
        device: uniqueDevices[i],
        iops_growth: parseInt(Math.round(iopsGrowth * 100) / 100),
        total_used_growth: parseInt(Math.round(totalUsedGrowth * 100) / 100)
      });
    }

    let dataArray = [];

    // Loop to get the info needed for graph
    for (let i = 0; i < newData.length; i++) {
      let graphInfo = (({
                          iops_growth,
                          total_used_growth
                        }) => ({
        total_used_growth,
        iops_growth
      }))(newData[i]);
      dataArray.push(graphInfo);
    }

    var finalArray = []; // For reorganized objects

    for (let y = 0; y < titles.length; y++) {
      // Array of objects > array of arrays
      var smallerArrays = [];
      for (let i = 0; i < dataArray.length; i++) {
        var values = Object.values(dataArray[i]);

        smallerArrays.push(values);
      }

      // Return just values by category
      var removedKeys = [];
      for (let i = 0; i < smallerArrays.length; i++) {
        var newArray = smallerArrays[i].map(data => {
          return data;
        });
        removedKeys.push(newArray[y]);
      }
      finalArray.push(removedKeys);
    }

    // For column chart axes
    if (finalArray[1].length === 1 && finalArray[1][0] > 0) {
      var axis2Min = 0;
      var axis2Max = finalArray[1][0];
    } else if (finalArray[1].length === 1 && finalArray[1][0] < 0) {
      axis2Max = 0;
      axis2Min = finalArray[1][0];
    } else {
      axis2Max = Math.max.apply(Math, finalArray[1]);
      axis2Min = Math.min.apply(Math, finalArray[1]);
    }

    if (finalArray[0].length === 1 && finalArray[0][0] > 0) {
      var axis1Min = 0;
      var axis1Max = finalArray[0][0];
    } else if (finalArray[0].length === 1 && finalArray[0][0] < 0) {
      axis1Max = 0;
      axis1Min = finalArray[0][0];
    } else {
      axis1Max = Math.max.apply(Math, finalArray[0]);
      axis1Min = Math.min.apply(Math, finalArray[0]);
    }
    setMaxMin({
      axis2Max: axis2Max || null,
      axis2Min: axis2Min || null,
      axis1Max: axis1Max || null,
      axis1Min: axis1Min || null
    });

    var chartInfo = [];
    for (let i = 0; i < titles.length; i++) {
      chartInfo.push({
        name: titles[i],
        data: finalArray[i],
        yAxis: i
      });
    }

    setChartSeries(chartInfo);
  };

  const getEnterprisePerformance = async function(data) {

    let dataGrid = data?.[0] ?? [];

    setEnterprisePerformance(dataGrid);
    const deviceData = dataGrid?.map?.(device => {
      return device?.[selectedType.field || "device_type_code"];
    });
    const uniqueDevices = deviceData?.filter?.((device, index) => {
      return deviceData.indexOf(device) === index;
    });
    setDevices(uniqueDevices);
    getGraphData(dataGrid, uniqueDevices);
    setIsLoading(false);

  };

  const setDate = (date, type) => {
    if (type === "start") {
      setStartDate(date);
      setParams({
        start_period: periodFromDate(date)
      });
    } else if (type === "end") {
      setEndDate(date);
      setParams({
        end_period: periodFromDate(date)
      });
    }
  };

  const optionClick = (e) => {
    const { textContent, value } = e.target;
    setDate(new Date(value), "start");
    setRangesValue(textContent);
  };

  const filterChanged = (eventData) => {
    let rowData = [];
    eventData.api.forEachNodeAfterFilter(node => {
      rowData.push(node.data);
    });

    const deviceData = rowData.map(device => {
      return device?.[selectedType.field || "device_type_code"];
    });
    let uniqueDevices = deviceData.filter((device, index) => {
      return deviceData.indexOf(device) === index;
    });
    setDevices(uniqueDevices);
    getGraphData(rowData || [], uniqueDevices);
  };

  const onRowDoubleClicked = (e) => {
    navigate(`/reports/storage/device/device-summary?device_name=${encodeURI(e.data.device_name)}&wwn=${encodeURI(e.data.wwn)}&period_id=${e.data.period_id}`);
  };

  const onTypeChange = e => {
    setParams({
      type: e.value
    });
  };

  return (
    <Card className={`report-card p-2 nav-card-${theme}`}>
      <VsiH4>Enterprise Delta Report</VsiH4>
      {isLoading ? <Spinner color={"primary"} /> : (
        <div>
          <VsiBubble noAutobot>
            <Row>
              <Col>
                <div className="d-flex flex-row align-items-center ms-1">
                  <VsiLabeledSelect options={allBus}
                                    minWidth={225}
                                    label={"Select Type"}
                                    onChange={onTypeChange}
                                    defaultValue={selectedType} />
                  <div className="d-flex flex-column">
                    <VsiSelectLabel>
                      Select Increment
                    </VsiSelectLabel>
                    <VsiDropdown
                      isOpen={presetsOpen}
                      toggle={() => setPresetsOpen(!presetsOpen)}
                      direction={"down"}
                      // disabled={rangeOptions.length === 0 ? true : false}
                      caret
                      toggleStyle={{
                        backgroundColor: style[theme].altBgColor,
                        border: "none",
                        color: style[theme].textColor
                      }}
                      toggleVal={rangesValue}
                      menuStyling={{
                        overflowY: "scroll",
                        maxHeight: "400px",
                        backgroundColor: style[theme].altBgColor
                      }}
                      dropdownMenuStyle={{backgroundColor: style[theme].altBgColor}}
                    >
                      {rangeOptions.map(option => {
                        return <DropdownItem key={option.value} value={option.value}
                                             onClick={optionClick}
                                             style={{
                                               color: style[theme].textColor,
                                               backgroundColor: style[theme].altBgColor,
                                             }}>{option.label}</DropdownItem>;
                      })}
                    </VsiDropdown>
                  </div>
                </div>
              </Col>
              <Col xs="3">
                <Row>
                  <Col xs="4">
                    <VsiSelectLabel>
                      Start
                    </VsiSelectLabel>
                    <ReactDatePicker
                      includeDates={dateList}
                      selected={startDate}
                      onChange={(date) => setDate(date, "start")}
                      customInput={<CustomDateInput />}
                    />
                  </Col>
                  <Col xs="1">
                    <p>→</p>
                  </Col>
                  <Col xs="4">
                    <VsiSelectLabel>
                      End
                    </VsiSelectLabel>
                    <ReactDatePicker
                      includeDates={dateList}
                      selected={endDate}
                      onChange={(date) => setDate(date, "end")}
                      customInput={<CustomDateInput />}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </VsiBubble>
          <VsiBubble autobotInfo={{
            secondaryMetric: [rangesValue, startDate, endDate],
            data: enterprisePerformance,
            yValue: ["usable_gb_growth", "iops_growth"],
            yAxes: ["GiB", "IOPs"]
          }}>
            <VerticalBarChart
              categories={devices}
              height={600}
              series={chartSeries}
              includeLegend={true}
              changeAxisTitle={true}
              axisMaxMin={maxMin}
              xTitle={selectedType.label}
              yTitle1={"GiB"}
              yTitle2={"IOPs"}
            />
          </VsiBubble>
          <VsiBubble>
            <Grid
              data={enterprisePerformance}
              columnDefs={enterprisePerformanceDefs}
              filterChanged={filterChanged}
              rowDoubleClicked={onRowDoubleClicked}
            />
          </VsiBubble>
        </div>
      )}
    </Card>
  );
}
